import { combineEpics } from 'redux-observable';
import { from, Observable, of } from 'rxjs';
import { catchError, filter, switchMap, withLatestFrom } from 'rxjs/operators';
import { isActionOf } from 'typesafe-actions';

import { selectSelectedProvider } from 'rdx/account';
import { ErrorReporter } from '@provider-portal/common/utils/errorReporting';
import { GlobalAppActions } from 'rdx/actions';
import { AppServices } from 'rdx/middleware';
import { GlobalAppState } from 'rdx/state';
import {
  getMainEntryWidgetUrlAsyncAction,
  getSupportCaseWidgetUrlAsyncAction,
  getSupportWidgetMqttConnectionInfoAsyncAction,
  getUnreadCasesWithLastMessageAsyncAction,
} from 'rdx/support/actions';

const getMainEntryWidgetUrlAsyncActionEpic = (
  action$: Observable<GlobalAppActions>,
  state$: Observable<GlobalAppState>,
  { apiClient }: AppServices,
): Observable<GlobalAppActions> => {
  return action$.pipe(
    filter(isActionOf(getMainEntryWidgetUrlAsyncAction.request)),
    withLatestFrom(state$),
    switchMap(([_action, state]) => {
      const selectedProvider = selectSelectedProvider(state);
      if (!selectedProvider) {
        return of(getMainEntryWidgetUrlAsyncAction.failure(new Error('No selected provider')));
      }
      if (!selectedProvider.providerId) {
        return of(
          getMainEntryWidgetUrlAsyncAction.failure(new Error('Missing selected provider id')),
        );
      }
      if (!selectedProvider.cityId) {
        return of(
          getMainEntryWidgetUrlAsyncAction.failure(new Error('Missing selected provider city id')),
        );
      }

      return from(
        apiClient.deliveryProviderPortal.getMainEntryWidgetUrl({
          provider_id: selectedProvider.providerId,
          city_id: selectedProvider.cityId,
        }),
      ).pipe(
        switchMap((response) => {
          return of(getMainEntryWidgetUrlAsyncAction.success(response));
        }),
        catchError((error) => {
          ErrorReporter.captureException(error);
          return of(getMainEntryWidgetUrlAsyncAction.failure(error));
        }),
      );
    }),
  );
};

const getSupportCaseWidgetUrlAsyncActionEpic = (
  action$: Observable<GlobalAppActions>,
  state$: Observable<GlobalAppState>,
  { apiClient }: AppServices,
): Observable<GlobalAppActions> => {
  return action$.pipe(
    filter(isActionOf(getSupportCaseWidgetUrlAsyncAction.request)),
    withLatestFrom(state$),
    switchMap(([_action, state]) => {
      const selectedProvider = selectSelectedProvider(state);
      if (!selectedProvider) {
        return of(getSupportCaseWidgetUrlAsyncAction.failure(new Error('No selected provider')));
      }
      if (!selectedProvider.providerId) {
        return of(
          getSupportCaseWidgetUrlAsyncAction.failure(new Error('Missing selected provider id')),
        );
      }
      if (!selectedProvider.cityId) {
        return of(
          getSupportCaseWidgetUrlAsyncAction.failure(
            new Error('Missing selected provider city id'),
          ),
        );
      }

      return from(
        apiClient.deliveryProviderPortal.getSupportCaseWidgetUrl({
          provider_id: selectedProvider.providerId,
          city_id: selectedProvider.cityId,
        }),
      ).pipe(
        switchMap((response) => {
          return of(getSupportCaseWidgetUrlAsyncAction.success(response));
        }),
        catchError((error) => {
          ErrorReporter.captureException(error);
          return of(getSupportCaseWidgetUrlAsyncAction.failure(error));
        }),
      );
    }),
  );
};

const getUnreadCasesWithLastMessageAsyncActionEpic = (
  action$: Observable<GlobalAppActions>,
  state$: Observable<GlobalAppState>,
  { apiClient }: AppServices,
): Observable<GlobalAppActions> => {
  return action$.pipe(
    filter(isActionOf(getUnreadCasesWithLastMessageAsyncAction.request)),
    withLatestFrom(state$),
    switchMap(([_action, state]) => {
      const selectedProvider = selectSelectedProvider(state);
      if (!selectedProvider) {
        return of(
          getUnreadCasesWithLastMessageAsyncAction.failure(new Error('No selected provider')),
        );
      }
      if (!selectedProvider.providerId) {
        return of(
          getUnreadCasesWithLastMessageAsyncAction.failure(
            new Error('Missing selected provider id'),
          ),
        );
      }

      return from(
        apiClient.deliveryProviderPortal.getUnreadCasesWithLastMessage({
          provider_id: selectedProvider.providerId,
        }),
      ).pipe(
        switchMap((response) => {
          return of(getUnreadCasesWithLastMessageAsyncAction.success(response));
        }),
        catchError((error) => {
          ErrorReporter.captureException(error);
          return of(getUnreadCasesWithLastMessageAsyncAction.failure(error));
        }),
      );
    }),
  );
};

const getSupportWidgetMqttConnectionInfoAsyncActionEpic = (
  action$: Observable<GlobalAppActions>,
  state$: Observable<GlobalAppState>,
  { apiClient }: AppServices,
): Observable<GlobalAppActions> => {
  return action$.pipe(
    filter(isActionOf(getSupportWidgetMqttConnectionInfoAsyncAction.request)),
    withLatestFrom(state$),
    switchMap(([_action, state]) => {
      const selectedProvider = selectSelectedProvider(state);
      if (!selectedProvider) {
        return of(
          getSupportWidgetMqttConnectionInfoAsyncAction.failure(new Error('No selected provider')),
        );
      }
      if (!selectedProvider.providerId) {
        return of(
          getSupportWidgetMqttConnectionInfoAsyncAction.failure(
            new Error('Missing selected provider id'),
          ),
        );
      }

      return from(
        apiClient.deliveryProviderPortal.getSupportWidgetMqttConnectionInfo({
          provider_id: selectedProvider.providerId,
        }),
      ).pipe(
        switchMap((response) => {
          return of(getSupportWidgetMqttConnectionInfoAsyncAction.success(response));
        }),
        catchError((error) => {
          ErrorReporter.captureException(error);
          return of(getSupportWidgetMqttConnectionInfoAsyncAction.failure(error));
        }),
      );
    }),
  );
};

export const supportEpic = combineEpics(
  getMainEntryWidgetUrlAsyncActionEpic,
  getSupportCaseWidgetUrlAsyncActionEpic,
  getUnreadCasesWithLastMessageAsyncActionEpic,
  getSupportWidgetMqttConnectionInfoAsyncActionEpic,
);
